import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { PodcastEpisode, PodcastEpisodeInsert } from '@/types/podcastHost';
import { toast } from 'sonner';

export const usePodcastEpisodes = (brandId?: string, showId?: string) => {
  return useQuery({
    queryKey: ['podcast-episodes', brandId, showId],
    queryFn: async () => {
      if (!brandId) return [];
      
      let query = supabase
        .from('os_podcast_shows_episodes')
        .select(`
          *,
          show:os_podcast_shows(id, name, thumbnail)
        `)
        .eq('brand_id', brandId)
        .order('target_record_date', { ascending: true, nullsFirst: false });

      if (showId) {
        query = query.eq('show_id', showId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as unknown as PodcastEpisode[];
    },
    enabled: !!brandId,
  });
};

export const usePodcastEpisode = (episodeId?: string) => {
  return useQuery({
    queryKey: ['podcast-episode', episodeId],
    queryFn: async () => {
      if (!episodeId) return null;
      
      const { data, error } = await supabase
        .from('os_podcast_shows_episodes')
        .select(`
          *,
          show:os_podcast_shows(*)
        `)
        .eq('id', episodeId)
        .single();

      if (error) throw error;
      return data as unknown as PodcastEpisode;
    },
    enabled: !!episodeId,
  });
};

export const useCreatePodcastEpisode = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (episodeData: PodcastEpisodeInsert) => {
      const { data, error } = await supabase
        .from('os_podcast_shows_episodes')
        .insert(episodeData as any)
        .select()
        .single();

      if (error) throw error;
      return data as unknown as PodcastEpisode;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['podcast-episodes', data.brand_id] });
      queryClient.invalidateQueries({ queryKey: ['podcast-episodes', data.brand_id, data.show_id] });
      toast.success('Episode created successfully');
    },
    onError: (error) => {
      console.error('Error creating episode:', error);
      toast.error('Failed to create episode');
    },
  });
};

export const useUpdatePodcastEpisode = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<PodcastEpisode> }) => {
      // Only send valid DB columns, exclude joined data
      const { show, guests, guest, ...dbUpdates } = updates;
      
      const { data, error } = await supabase
        .from('os_podcast_shows_episodes')
        .update(dbUpdates as any)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as unknown as PodcastEpisode;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['podcast-episodes', data.brand_id] });
      queryClient.invalidateQueries({ queryKey: ['podcast-episode', data.id] });
      toast.success('Episode updated successfully');
    },
    onError: (error) => {
      console.error('Error updating episode:', error);
      toast.error('Failed to update episode');
    },
  });
};

export const useDeletePodcastEpisode = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, brandId, showId }: { id: string; brandId: string; showId: string }) => {
      const { error } = await supabase
        .from('os_podcast_shows_episodes')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { brandId, showId };
    },
    onSuccess: ({ brandId, showId }) => {
      queryClient.invalidateQueries({ queryKey: ['podcast-episodes', brandId] });
      queryClient.invalidateQueries({ queryKey: ['podcast-episodes', brandId, showId] });
      toast.success('Episode deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting episode:', error);
      toast.error('Failed to delete episode');
    },
  });
};
